import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./WalletAccess.css";

const OTP_LENGTH = 6;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function WalletAccess({ apiBaseUrl }) {
  const [step, setStep] = useState("otp");
  const [otpDigits, setOtpDigits] = useState(() => Array(OTP_LENGTH).fill(""));
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOtpFadingOut, setIsOtpFadingOut] = useState(false);
  const [resendState, setResendState] = useState("idle");
  const [resentAtText, setResentAtText] = useState("");
  const otpRefs = useRef(Array.from({ length: OTP_LENGTH }, () => null));

  const [walletSets, setWalletSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [walletLoading, setWalletLoading] = useState(false);
  const [isCreatingSet, setIsCreatingSet] = useState(false);
  const [isSavingEntry, setIsSavingEntry] = useState(false);
  const [showCreateSet, setShowCreateSet] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newSetName, setNewSetName] = useState("");
  const [entryKey, setEntryKey] = useState("");
  const [entryValue, setEntryValue] = useState("");
  const [visibleEntries, setVisibleEntries] = useState({});

  const selectedSet = useMemo(
    () => walletSets.find((set) => set._id === selectedSetId) || null,
    [walletSets, selectedSetId]
  );

  const loadWalletSets = useCallback(async () => {
    setWalletLoading(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/wallet`);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setMessage(data.message || "Could not load wallet sets.");
        return;
      }

      const nextSets = Array.isArray(data.sets) ? data.sets : [];
      setWalletSets(nextSets);
      setSelectedSetId((previousSetId) =>
        nextSets.some((set) => set._id === previousSetId) ? previousSetId : ""
      );
    } catch (error) {
      setMessage("Could not load wallet sets.");
    } finally {
      setWalletLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    if (step !== "wallet") {
      return;
    }

    loadWalletSets();
  }, [step, loadWalletSets]);

  useEffect(() => {
    setVisibleEntries({});
    setShowAddEntry(false);
    setEntryKey("");
    setEntryValue("");
  }, [selectedSetId]);

  function handleOtpChange(index, value) {
    const nextDigit = value.replace(/\D/g, "").slice(-1);

    setOtpDigits((previousDigits) => {
      const nextDigits = [...previousDigits];
      nextDigits[index] = nextDigit;
      return nextDigits;
    });

    if (nextDigit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  }

  function handleOtpKeyDown(index, event) {
    if (event.key !== "Backspace") {
      return;
    }

    event.preventDefault();

    setOtpDigits((previousDigits) => {
      const nextDigits = [...previousDigits];
      if (nextDigits[index]) {
        nextDigits[index] = "";
      } else if (index > 0) {
        nextDigits[index - 1] = "";
        otpRefs.current[index - 1]?.focus();
      }
      return nextDigits;
    });
  }

  function handleOtpPaste(event) {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const pastedDigits = pastedText.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");

    if (pastedDigits.length === 0) {
      return;
    }

    const nextDigits = Array(OTP_LENGTH).fill("");
    pastedDigits.forEach((digit, index) => {
      nextDigits[index] = digit;
    });

    setOtpDigits(nextDigits);
    otpRefs.current[Math.min(pastedDigits.length, OTP_LENGTH) - 1]?.focus();
  }

  async function handleResend() {
    if (resendState === "loading") {
      return;
    }

    setResendState("loading");
    await delay(2000);
    setResentAtText(
      new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
    setResendState("idle");
  }

  async function verifyOtp(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage("");
    const otp = otpDigits.join("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/wallet/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setMessage(data.message || "OTP verification failed.");
        return;
      }

      await delay(800);
      setIsOtpFadingOut(true);
      await delay(220);
      setStep("wallet");
      setShowCreateSet(false);
      setShowAddEntry(false);
      setMessage("");
    } catch (error) {
      setMessage("Could not connect to wallet service.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCreateSet(event) {
    event.preventDefault();

    const name = newSetName.trim();
    if (!name) {
      setMessage("Set name is required.");
      return;
    }

    setIsCreatingSet(true);
    setMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/wallet/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setMessage(data.message || "Could not create wallet set.");
        return;
      }

      const createdSet = data.set;
      if (createdSet?._id) {
        setWalletSets((previousSets) => [createdSet, ...previousSets]);
        setSelectedSetId(createdSet._id);
      } else {
        await loadWalletSets();
      }

      setNewSetName("");
      setShowCreateSet(false);
    } catch (error) {
      setMessage("Could not create wallet set.");
    } finally {
      setIsCreatingSet(false);
    }
  }

  async function handleSaveEntry(event) {
    event.preventDefault();

    const key = entryKey.trim();
    const value = entryValue.trim();

    if (!selectedSetId || !key || !value) {
      setMessage("Key and value are required.");
      return;
    }

    setIsSavingEntry(true);
    setMessage("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/wallet/entry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          setId: selectedSetId,
          key,
          value,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        setMessage(data.message || "Could not add wallet entry.");
        return;
      }

      if (data.set?._id) {
        setWalletSets((previousSets) =>
          previousSets.map((set) => (set._id === data.set._id ? data.set : set))
        );
      } else {
        await loadWalletSets();
      }

      setEntryKey("");
      setEntryValue("");
      setShowAddEntry(false);
    } catch (error) {
      setMessage("Could not add wallet entry.");
    } finally {
      setIsSavingEntry(false);
    }
  }

  function toggleEntryVisibility(entryId) {
    setVisibleEntries((previous) => ({
      ...previous,
      [entryId]: !previous[entryId],
    }));
  }

  function handleLockWallet() {
    setStep("otp");
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setMessage("");
    setIsSubmitting(false);
    setIsOtpFadingOut(false);
    setResendState("idle");
    setResentAtText("");
    setShowCreateSet(false);
    setShowAddEntry(false);
    setNewSetName("");
    setEntryKey("");
    setEntryValue("");
    setSelectedSetId("");
    setVisibleEntries({});
  }

  if (step === "otp") {
    return (
      <div className="wallet-auth-shell">
        <div className={`wallet-auth-card ${isOtpFadingOut ? "fade-out" : ""}`}>
          <p className="wallet-auth-label">Wallet</p>
          <h2 className="wallet-auth-title">Enter Secure Verification Code</h2>

          <form className="wallet-auth-form" onSubmit={verifyOtp}>
            <div className="wallet-otp-row" onPaste={handleOtpPaste}>
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    otpRefs.current[index] = element;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(event) => handleOtpChange(index, event.target.value)}
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  className={`wallet-otp-input ${digit ? "filled" : ""}`}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <button type="submit" className="wallet-auth-btn" disabled={isSubmitting}>
              <span className="wallet-auth-btn-inner">
                {isSubmitting && <span className="wallet-spinner" />}
                {isSubmitting ? "Verifying..." : "Verify"}
              </span>
            </button>

            <div className="wallet-resend-wrap">
              <button
                type="button"
                className="wallet-resend-btn"
                onClick={handleResend}
                disabled={resendState === "loading"}
              >
                {resendState === "loading" ? "Sending..." : "Resend Code"}
              </button>
            </div>
            {resentAtText && (
              <p className="wallet-resend-sent">OTP sent at {resentAtText}</p>
            )}
          </form>

          {message && <p className="wallet-auth-error">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-notes-layout">
      <section className="notes-panel wallet-sets-panel">
        <div className="notes-panel-header">
          <div className="notes-panel-title-wrap">
            <div>
              <p className="panel-kicker">Wallet</p>
              <h2>Sets</h2>
            </div>
          </div>
          <button
            type="button"
            className="new-note-btn"
            onClick={() => setShowCreateSet((previous) => !previous)}
          >
            {showCreateSet ? "Close" : "+ Create Set"}
          </button>
        </div>

        {showCreateSet && (
          <form className="wallet-inline-form" onSubmit={handleCreateSet}>
            <input
              type="text"
              value={newSetName}
              onChange={(event) => setNewSetName(event.target.value)}
              placeholder="Set name (e.g., Gmail)"
              className="wallet-input"
            />
            <div className="wallet-inline-actions">
              <button type="submit" className="primary-btn" disabled={isCreatingSet}>
                {isCreatingSet ? "Saving..." : "Save Set"}
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setShowCreateSet(false);
                  setNewSetName("");
                }}
                disabled={isCreatingSet}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="notes-list">
          {walletLoading && <p className="muted-text">Loading sets...</p>}
          {!walletLoading && walletSets.length === 0 && (
            <p className="muted-text">No sets found. Create your first set.</p>
          )}

          {walletSets.map((set) => (
            <button
              key={set._id}
              type="button"
              className={`note-item ${selectedSetId === set._id ? "selected" : ""}`}
              onClick={() => setSelectedSetId(set._id)}
            >
              <div className="note-item-top">
                <h3>{set.name}</h3>
                <span>{(set.entries || []).length}</span>
              </div>
              <p>{(set.entries || []).length} saved entries</p>
            </button>
          ))}
        </div>
      </section>

      <main className="editor-panel wallet-entries-panel">
        <div className="editor-top">
          <p className="panel-kicker">{selectedSet ? selectedSet.name : "Wallet"}</p>
          {message && <p className="status-chip">{message}</p>}
        </div>

        {!selectedSet ? (
          <p className="muted-text">Select a set from left panel to view entries.</p>
        ) : (
          <>
            <div className="wallet-entry-header">
              <h2 className="wallet-entry-title">{selectedSet.name}</h2>
              <button
                type="button"
                className="primary-btn"
                onClick={() => setShowAddEntry((previous) => !previous)}
              >
                {showAddEntry ? "Close" : "+ Add Entry"}
              </button>
            </div>

            {showAddEntry && (
              <form className="wallet-inline-form" onSubmit={handleSaveEntry}>
                <input
                  type="text"
                  value={entryKey}
                  onChange={(event) => setEntryKey(event.target.value)}
                  placeholder="Key (e.g., Email)"
                  className="wallet-input"
                />
                <input
                  type="text"
                  value={entryValue}
                  onChange={(event) => setEntryValue(event.target.value)}
                  placeholder="Value"
                  className="wallet-input"
                />
                <div className="wallet-inline-actions">
                  <button type="submit" className="primary-btn" disabled={isSavingEntry}>
                    {isSavingEntry ? "Saving..." : "Save Entry"}
                  </button>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => {
                      setShowAddEntry(false);
                      setEntryKey("");
                      setEntryValue("");
                    }}
                    disabled={isSavingEntry}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="wallet-entry-list">
              {(selectedSet.entries || []).length === 0 && (
                <p className="muted-text">No entries yet. Add your first key-value item.</p>
              )}

              {(selectedSet.entries || []).map((entry, index) => {
                const entryId = entry._id || `${index}`;
                const isVisible = Boolean(visibleEntries[entryId]);

                return (
                  <article key={entryId} className="wallet-entry-item">
                    <div>
                      <p className="wallet-entry-key">{entry.key}</p>
                      <p className="wallet-entry-value">{isVisible ? entry.value : "****"}</p>
                    </div>
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => toggleEntryVisibility(entryId)}
                    >
                      {isVisible ? "Hide" : "Show"}
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </main>

      <button
        type="button"
        className="wallet-lock-fab"
        onClick={handleLockWallet}
      >
        Lock Wallet
      </button>
    </div>
  );
}
